import { t } from "i18next";

/**
 * Converts a Date object to yyyy-mm-dd string in local time (no UTC offset).
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Formats an ISO date string as the lab-card top label, e.g. "12 Feb 2025".
 * Returns "" for null/empty/unparseable input so callers can render the
 * label conditionally. Shared by the lab-insights parameter cards (landing
 * pinned grid, group details) so every card shows the report date the same
 * way. Mirrors the `format(parseISO(iso), "dd MMM yyyy")` the lab pages
 * used inline, without pulling date-fns into the card component.
 */
export function formatLabCardDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Formats a date value as 'Dated 12 Feb 2025'.
 */
export function formatDocumentDateShort(
  dateValue: string | number | Date,
  ignoreText?: boolean
): string {
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return ignoreText ? `${day} ${month} ${year}` : `${day} ${month} ${year}`;
}

/**
 * Returns the day of the month with ordinal suffix (e.g., 1st, 2nd, 3rd, 4th, etc.)
 */
export function getOrdinal(n: number): string {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getPaddedDay(day: number): string {
  return day < 10 ? `0${day}` : day.toString();
}

/**
 * Formats a date value as '6th June 2025'.
 */
export function formatPrettyDate(dateValue: string | number | Date): string {
  const date = new Date(dateValue);
  const day = getOrdinal(date.getDate());
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Formats a date value as '6 June 2025' (without ordinal suffix).
 */
export function formatPrettyDateNoOrdinal(
  dateValue: string | number | Date
): string {
  const date = new Date(dateValue);
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function formatReminderDate(dateValue: string | number | Date): string {
  const date = new Date(dateValue);
  const day = getPaddedDay(date.getDate());
  const month = date.toLocaleString("default", { month: "short" });
  return `${day} ${month}`;
}

export function formatTime(dateValue: string | number | Date): string {
  const date = new Date(dateValue);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  // format time as 12 hour format
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12;
  // ensure minutes are always 2 digits
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  // ensure hours are always 2 digits
  const formattedHours2 =
    formattedHours < 10 ? `0${formattedHours}` : formattedHours;
  return `${formattedHours2}:${formattedMinutes} ${ampm}`;
}

export function formatReminderDateWithTimezone(
  dateValue: string | number | Date,
  timezone?: string
): string {
  const date = new Date(dateValue);
  const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Use timezone-aware formatting for both day and month
  const day = getPaddedDay(
    parseInt(
      date.toLocaleString("en-US", {
        timeZone,
        day: "numeric",
      })
    )
  );
  const month = date.toLocaleString("default", {
    month: "short",
    timeZone,
  });
  const year = parseInt(
    date.toLocaleString("en-US", { timeZone, year: "numeric" })
  );
  const currentYear = new Date().getFullYear();
  return year !== currentYear ? `${day} ${month} ${year}` : `${day} ${month}`;
}

export function formatTimeWithTimezone(
  dateValue: string | number | Date,
  timezone?: string
): string {
  const date = new Date(dateValue);
  const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Use Intl.DateTimeFormat to get timezone-aware time
  const timeString = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timeZone,
  });

  return timeString;
}

export function inputFieldDate(dateValue: string | number | Date): string {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${month} ${year}`;
}

export function initDate(
  dateValue: string | number | Date,
  timezone?: string
): Date | null {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;

  const date = new Date(dateValue);

  // If timezone is provided, convert the date to that timezone
  if (timezone) {
    // Use Intl.DateTimeFormat to get the timezone-aware date components
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const year = parseInt(parts.find((p) => p.type === "year")?.value || "0");
    const month =
      parseInt(parts.find((p) => p.type === "month")?.value || "0") - 1; // JS months are 0-indexed
    const day = parseInt(parts.find((p) => p.type === "day")?.value || "0");
    const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0");
    const minute = parseInt(
      parts.find((p) => p.type === "minute")?.value || "0"
    );
    const second = parseInt(
      parts.find((p) => p.type === "second")?.value || "0"
    );

    return new Date(year, month, day, hour, minute, second);
  }

  return date;
}

export function inputFieldTime(
  dateValue: string | number | Date,
  defaultTime?: string,
  is24Hour: boolean = false,
  timezone?: string
): string {
  if (!dateValue) return defaultTime || "";
  const date = new Date(dateValue);

  // If timezone is provided, use timezone-aware time formatting
  if (timezone) {
    if (is24Hour) {
      const timeString = date.toLocaleString("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return timeString;
    } else {
      const timeString = date.toLocaleString("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return timeString;
    }
  }

  // Fallback to original behavior if no timezone provided
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (is24Hour) {
    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${formattedHours}:${formattedMinutes}`;
  }
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12;
  const formattedHours2 =
    formattedHours < 10 ? `0${formattedHours}` : formattedHours;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${formattedHours2}:${formattedMinutes} ${ampm}`;
}

export function updateTime(
  dateValue: string | number | Date,
  timeValue: string
): Date {
  const date = new Date(dateValue);
  if (!timeValue) return date;
  // handle 12 hour format like 02:30 PM
  if (timeValue.includes("AM") || timeValue.includes("PM")) {
    const [time, ampm] = timeValue.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    date.setHours(hours);
    date.setMinutes(minutes);
    if (ampm === "PM" && hours !== 12) {
      date.setHours(date.getHours() + 12);
    } else if (ampm === "AM" && hours === 12) {
      date.setHours(0);
    }
    return date;
  }
  const [hours, minutes] = timeValue.split(":").map(Number);
  date.setHours(hours);
  date.setMinutes(minutes);
  return date;
}

export function updateDate(
  dateValue: string | number | Date,
  dateValue2: string
): Date {
  const date = new Date(dateValue);
  const [year, month, day] = dateValue2.split("-").map(Number);
  date.setFullYear(year);
  date.setMonth(month - 1);
  date.setDate(day);
  return date;
}

// given the time in 12 hour format, add hours to it and max it to 24 hours
export function addHours(timeStr: string, hoursToAdd: number): string {
  // eslint-disable-next-line prefer-const -- `time` could be const but destructured with `ampm` which is reassigned below
  let [time, ampm] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);
  if (ampm === "PM") {
    hours += 12;
  }
  hours += hoursToAdd;
  if (hours > 24) {
    hours = 24;
    if (minutes > 0) {
      minutes = 0;
    }
  }
  if (hours > 12) {
    hours -= 12;
    ampm = "PM";
  }

  let hoursStr = hours.toString();
  if (hours < 10) {
    hoursStr = `0${hours}`;
  }

  let minutesStr = minutes.toString();
  if (minutes < 10) {
    minutesStr = `0${minutes}`;
  }

  return `${hoursStr}:${minutesStr} ${ampm}`;
}

export function formatMonthYear(month: number, year: number) {
  // month is 1–12, but JS Date months are 0–11
  const date = new Date(year, month - 1);

  // Use toLocaleString to get the short month name
  const monthName = date.toLocaleString("default", { month: "short" });

  return `${monthName} ${year}`;
}

/**
 * Creates a JS Date from year, 1-based month (Jan=1, Dec=12), and day.
 * Example: dateFromYMD(2028, 12, 15) => Dec 15, 2028
 */
export function dateFromYMD(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day);
}

export function formatWeeksToAge(weeks: number | string): string {
  const weeksNumber = Number(weeks);
  const years = Math.floor(weeksNumber / 52);
  const remainingWeeks = weeksNumber % 52;
  const months = Math.floor(remainingWeeks / 4);
  const weeksLeft = remainingWeeks % 4;
  let ageString = "";
  if (years > 0) {
    ageString += `${years} ${
      years === 1 ? t("common.year") : t("common.years")
    }`;
  }
  if (months > 0) {
    ageString += ` ${months} ${
      months === 1 ? t("common.month") : t("common.months")
    }`;
  }
  if (weeksLeft > 0) {
    ageString += ` ${weeksLeft} ${
      weeksLeft === 1 ? t("common.week") : t("common.weeks")
    }`;
  }
  return ageString;
}

// Utility functions for date formatting
/**
 * Formats a date as '5 Sep 2025 at 2:13 PM' for comment timestamps.
 */
export function formatCommentDate(dateVal?: string | Date): string {
  if (!dateVal) return "";
  const date = typeof dateVal === "string" ? new Date(dateVal) : dateVal;
  return `${date.getDate()} ${date.toLocaleString("en-US", {
    month: "short",
  })} ${date.getFullYear()} at ${date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
}

/**
 * Coerces a stored time value into the canonical padded 12-hour
 * "hh:mm AM/PM" shape that the reminder time pickers, `updateTime` and
 * `isTimeBefore` all assume (DEFAULT_TIME_12HR is "09:00 AM").
 *
 * Needed because a reminder's `dayFrequencyValue` can also hold 24-hour
 * "HH:mm" — Pillbox stores its slots that way, and the pillbox → reminder
 * schedule sync used to copy them across verbatim. Feeding "13:00" straight
 * into a picker is worse than a display glitch: `isTimeBefore` has no
 * 24-hour branch and silently scores it as midnight, so dose-ordering
 * validation and the pre-filled picker disagree with each other.
 * Returns null for anything unparseable so callers can fall back.
 */
export function normalizeTo12HourTime(
  value: string | number | null | undefined
): string | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const match12 = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    const hour = Number(match12[1]);
    if (hour < 1 || hour > 12 || Number(match12[2]) > 59) return null;
    return `${String(hour).padStart(2, "0")}:${
      match12[2]
    } ${match12[3].toUpperCase()}`;
  }

  const match24 = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hour24 = Number(match24[1]);
    if (hour24 > 23 || Number(match24[2]) > 59) return null;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${String(hour24 % 12 || 12).padStart(2, "0")}:${
      match24[2]
    } ${ampm}`;
  }

  // Legacy seconds-since-midnight.
  if (/^\d+$/.test(raw)) {
    const totalSeconds = Number(raw);
    if (totalSeconds < 0 || totalSeconds >= 86400) return null;
    const hour24 = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${String(hour24 % 12 || 12).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")} ${ampm}`;
  }

  return null;
}

export function isTimeBefore(time1: string, time2: string): boolean {
  // handle 12 hour format like 02:30 PM
  let hours1 = 0;
  let minutes1 = 0;
  let hours2 = 0;
  let minutes2 = 0;
  if (time1.includes("AM") || time1.includes("PM")) {
    const [time, ampm] = time1.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    hours1 = hours;
    if (ampm === "PM" && hours1 !== 12) {
      hours1 += 12;
    } else if (ampm === "AM" && hours1 === 12) {
      hours1 = 0;
    }
    minutes1 = minutes;
  }
  if (time2.includes("AM") || time2.includes("PM")) {
    const [time, ampm] = time2.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    hours2 = hours;
    if (ampm === "PM" && hours2 !== 12) {
      hours2 += 12;
    } else if (ampm === "AM" && hours2 === 12) {
      hours2 = 0;
    }
    minutes2 = minutes;
  }
  return hours1 < hours2 || (hours1 === hours2 && minutes1 < minutes2);
}

export const formattedYear = (dateValue: string | number | Date): string => {
  const date = new Date(dateValue);
  return date.getFullYear().toString();
};

export const sortByYearMonth = <
  T extends {
    year?: number;
    month?: number;
    date?: string;
    diagnosisYear?: number;
    diagnosisMonth?: number;
    diagnosisDate?: string;
    implantDate?: string;
  }
>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    const yearA =
      a.year ||
      a.diagnosisYear ||
      (a.date ? new Date(a.date).getFullYear() : 0) ||
      (a.diagnosisDate ? new Date(a.diagnosisDate).getFullYear() : 0) ||
      (a.implantDate ? new Date(a.implantDate).getFullYear() : 0);
    const yearB =
      b.year ||
      b.diagnosisYear ||
      (b.date ? new Date(b.date).getFullYear() : 0) ||
      (b.diagnosisDate ? new Date(b.diagnosisDate).getFullYear() : 0) ||
      (b.implantDate ? new Date(b.implantDate).getFullYear() : 0);

    if (yearA !== yearB) {
      return yearB - yearA; // Descending order
    }

    const monthA =
      a.month ||
      a.diagnosisMonth ||
      (a.date ? new Date(a.date).getMonth() + 1 : 0) ||
      (a.diagnosisDate ? new Date(a.diagnosisDate).getMonth() + 1 : 0) ||
      (a.implantDate ? new Date(a.implantDate).getMonth() + 1 : 0);
    const monthB =
      b.month ||
      b.diagnosisMonth ||
      (b.date ? new Date(b.date).getMonth() + 1 : 0) ||
      (b.diagnosisDate ? new Date(b.diagnosisDate).getMonth() + 1 : 0) ||
      (b.implantDate ? new Date(b.implantDate).getMonth() + 1 : 0);

    return monthB - monthA; // Descending order
  });
};

export const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export function calculateAccountAge(createdAt?: string | Date): string {
  if (!createdAt) return "0";

  try {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return String(diffDays);
  } catch {
    return "0";
  }
}
